/**
 * Pure domain module for the Document Audit tab.
 *
 * No React, no data layer — plain functions over plain objects so the unit
 * tests in `tests/auditModel.test.js` can exercise every status permutation
 * and error mapping without rendering a component (mirrors `sor.model.js`).
 */

import {JOB_STATUS_FALLBACK, JOB_STATUS_META, REJECTION_REASON_LABELS, RETENTION_LABELS} from "./uploads-simulator.constants";
import {normalizeSor, stripOnboardingComplete} from "./sor.model";

function isPlainObject(value) {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function isCancelledError(error) {
    return error?.code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.name === "AbortError";
}

/**
 * Shape one intake job into the row/pill model the audit table renders.
 * `completed` splits on `accepted`; unknown statuses fall back to the
 * neutral em-dash entry so a new server value never breaks the table.
 *
 * @param {object | null | undefined} job one entry from the jobs endpoint
 * @returns {{statusKey: string | null, statusLabel: string, statusTone: string, reasonLabel: string | null, retained: boolean, retentionLabel: string}}
 */
export function buildJobRowModel(job) {
    const status = job?.status ?? null;
    const statusKey = status === "completed" ? (job?.accepted === true ? "completed_accepted" : "completed_rejected") : status;
    const meta = (statusKey && JOB_STATUS_META[statusKey]) || JOB_STATUS_FALLBACK;
    const reason = job?.reason;

    return {
        statusKey: statusKey ?? null,
        statusLabel: meta.label,
        statusTone: meta.tone,
        reasonLabel: reason ? REJECTION_REASON_LABELS[reason] || String(reason) : null,
        retained: job?.retention === "s3" && !!job?.document_id,
        retentionLabel: RETENTION_LABELS[job?.retention] || RETENTION_LABELS.none
    };
}

/**
 * Map an error thrown by `fetchIntakeJobs` into the `{kind, title, detail}`
 * shape the audit panel renders. `cancelled` is silent — the controller
 * drops it without surfacing anything.
 *
 * @param {unknown} error
 * @returns {{kind: string, title: string, detail: string}}
 */
export function mapAuditJobsError(error) {
    if (isCancelledError(error)) {
        return {kind: "cancelled", title: "", detail: ""};
    }

    const status = error?.response?.status;
    const serverDetail = typeof error?.response?.data?.error === "string" ? error.response.data.error : null;

    if (status === 404) {
        return {kind: "not_found", title: "Member not found", detail: serverDetail || "No member with this id exists under this FI."};
    }
    if (status === 403) {
        return {kind: "access", title: "Access denied", detail: serverDetail || "You do not have access to view document intake for this FI."};
    }
    if (error?.request && !error?.response) {
        return {kind: "network", title: "Network error", detail: "Could not reach the service. Check your connection and try again."};
    }
    return {kind: "unknown", title: "Could not load uploads", detail: serverDetail || error?.message || "Unexpected error."};
}

/**
 * Map an error thrown by the document-content fetch. The request uses
 * `responseType: "blob"`, so the ERROR body is a Blob too — this mapper
 * deliberately never reads `.error` from it and maps by HTTP status only.
 *
 * @param {unknown} error
 * @returns {{kind: string, title: string, detail: string}}
 */
export function mapContentError(error) {
    if (isCancelledError(error)) {
        return {kind: "cancelled", title: "", detail: ""};
    }

    const status = error?.response?.status;

    if (status === 404) {
        return {kind: "gone", title: "Bytes unavailable", detail: "The stored document is gone — not retained in this deployment or expired from S3."};
    }
    if (status === 403) {
        return {kind: "access", title: "Access denied", detail: "You do not have access to view stored documents for this FI."};
    }
    if (error?.request && !error?.response) {
        return {kind: "network", title: "Network error", detail: "Could not reach the service. Check your connection and try again."};
    }
    return {kind: "unknown", title: "Preview failed", detail: "The stored document could not be loaded. Try selecting the row again."};
}

/**
 * Map an error thrown by `fetchMemberSor` into a toast message.
 *
 * @param {unknown} error
 * @returns {string}
 */
export function mapMemberSorError(error) {
    const status = error?.response?.status;
    const serverDetail = typeof error?.response?.data?.error === "string" ? error.response.data.error : null;

    if (status === 404) return "User not found for this FI.";
    if (status === 403) return serverDetail || "You do not have access to load member SORs for this FI.";
    if (error?.request && !error?.response) return "Could not reach the service. Check your connection and try again.";
    return serverDetail || "Could not load the member's SOR.";
}

/**
 * Normalize the members-search payload (`{items}` paginated or legacy
 * `{users}`) into the compact option rows the audit search list renders.
 *
 * @param {object | null | undefined} payload `fetchUsers` response
 * @returns {{clientId: string, name: string, email: string}[]}
 */
export function buildMemberOptions(payload) {
    const rows = payload?.items || payload?.users || [];
    if (!Array.isArray(rows)) return [];
    return rows
        .filter(row => isPlainObject(row) && row.id)
        .map(row => ({
            clientId: String(row.id),
            name: typeof row.name === "string" ? row.name : "",
            email: typeof row.email === "string" ? row.email : ""
        }));
}

/**
 * The member-SOR apply path, extracted pure: shape the `member-sor` payload
 * into what the controller feeds through the composer's existing
 * `normalizeSor → setSor` flow, plus the notes + unsaved-source indicator.
 * A member loaded from the core always lands with `onboarding_complete`
 * off, whatever their real settings say — the simulator flows author from a
 * not-yet-onboarded state, and the composer switch can re-enable it.
 *
 * @param {object | null | undefined} payload `{sor, meta}` from the endpoint
 * @param {string | null} [fallbackClientId] used when `meta.client_id` is absent
 * @returns {{sor: object, notes: string[], source: {clientId: string | null, memberName: string, loadedAt: string | null}}}
 */
export function buildMemberSorApplication(payload, fallbackClientId = null) {
    const meta = isPlainObject(payload?.meta) ? payload.meta : {};
    return {
        sor: stripOnboardingComplete(normalizeSor(payload?.sor)),
        notes: Array.isArray(meta.notes) ? meta.notes.filter(note => typeof note === "string" && note.trim() !== "") : [],
        source: {
            clientId: meta.client_id ?? fallbackClientId ?? null,
            memberName: typeof meta.member_name === "string" ? meta.member_name : "",
            loadedAt: meta.loaded_at ?? null
        }
    };
}

/**
 * Truncate a sha256 for display (`1a2b3c4d…9e0f`). Full value stays behind
 * the copy affordance.
 *
 * @param {string | null | undefined} sha
 * @returns {string}
 */
export function truncateSha(sha) {
    const value = typeof sha === "string" ? sha.trim() : "";
    if (!value) return "—";
    if (value.length <= 16) return value;
    return `${value.slice(0, 10)}…${value.slice(-6)}`;
}
